import styled, { css, keyframes } from "styled-components";
import { Tabs } from "antd";
import {
  brand,
  border,
  status,
  surface,
  text,
  typography,
  radius,
} from "../../theme/tokens";

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const IntakePageWrapper = styled.div`
  font-family: ${typography.fontFamily};
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${surface.surface};
`;

export const IntakeNavTabs = styled(Tabs)`
  &.ant-tabs {
    padding: 0 24px;
    background: ${surface.surface};
    border-bottom: 1px solid ${border.default};

    .ant-tabs-nav { margin: 0; }
    .ant-tabs-nav-list { gap: 0; }

    .ant-tabs-tab {
      padding: 14px 20px;
      margin: 0;
      .ant-tabs-tab-btn {
        font-family: ${typography.fontFamily};
        font-weight: ${typography.weight.medium};
        font-size: 14px;
        color: ${text.tertiary};
      }
    }
    .ant-tabs-tab:focus,
    .ant-tabs-tab:focus-visible { outline: none !important; box-shadow: none !important; }
    .ant-tabs-tab div { outline: none !important; }
    .ant-tabs-tab-active .ant-tabs-tab-btn { font-weight: ${typography.weight.semibold}; color: ${brand.primary} !important; }
    .ant-tabs-ink-bar { background: ${brand.primary}; height: 3px; }
  }
`;

export const TabBadge = styled.span`
  background: ${({ active }) => (active ? "#e8f0fe" : border.light)};
  color: ${({ active }) => (active ? brand.primary : text.tertiary)};
  font-size: 11px;
  font-weight: ${typography.weight.semibold};
  padding: 2px 8px;
  border-radius: ${radius.xl};
  margin-left: 6px;
`;

export const ViewContainer = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

// ---------------------------------------------------------------------------
// Assign view
// ---------------------------------------------------------------------------

export const AssignLayout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

export const AssignSidebar = styled.div`
  width: 300px;
  min-width: 300px;
  border-right: 1px solid ${border.default};
  display: flex;
  flex-direction: column;
  background: ${surface.surface};
  overflow-y: auto;
`;

export const AssignSidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid ${border.light};
`;

export const AssignSidebarTitle = styled.div`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${text.disabled};
`;

export const AssignSidebarMeta = styled.span`
  font-size: 12px;
  color: ${text.disabled};
`;

export const AssignSidebarBody = styled.div`
  flex: 1;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const AssignSidebarFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${border.light};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DocumentViewerPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #525659;
  overflow: hidden;
`;

export const ViewerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #3b3f42;
  color: ${text.onAccent};
  font-size: 13px;
  min-height: 40px;
`;

export const ViewerToggle = styled.button`
  padding: 5px 12px;
  border-radius: ${radius.sm};
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: ${({ active }) => (active ? "rgba(255,255,255,0.15)" : "transparent")};
  color: ${text.onAccent};
  font-family: ${typography.fontFamily};
  font-size: 12px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: rgba(255, 255, 255, 0.1); }
`;

export const ViewerBody = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
`;

export const PdfPlaceholder = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  text-align: center;
  padding: 40px;
`;

// Replaces the PDF pane when the intake pipeline says the file itself is
// unusable (zero bytes, an error page saved as .pdf). Without it the
// Apryse viewer spins forever on a file it can never render.
export const FailedFileNotice = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  text-align: center;
  color: ${text.secondary};
  font-size: 13px;
  line-height: 1.5;
`;

export const FailedFileTitle = styled.div`
  font-size: 16px;
  font-weight: ${typography.weight.semibold};
  color: ${status.failed.fg};
`;

export const FailedFileDetail = styled.div`
  max-width: 460px;
`;

// Sidebar strip above the form so the sorter sees the problem next to the
// Delete button even when the Markdown tab is open.
export const FailedFileBanner = styled.div`
  padding: 10px 12px;
  background: ${status.failed.bg};
  border: 1px solid ${status.failed.border};
  border-radius: ${radius.md};
  color: ${status.failed.fg};
  font-size: 12px;
  line-height: 1.45;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-weight: ${typography.weight.semibold};
  }
`;

export const MarkdownViewer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  padding: 32px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: ${surface.surface};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  line-height: 1.7;
  color: ${text.primary};
  max-width: 800px;
  margin: 0 auto;

  h1 { font-size: 20px; margin: 24px 0 12px; &:first-child { margin-top: 0; } }
  h2 { font-size: 16px; margin: 20px 0 10px; }
  h3 { font-size: 14px; margin: 16px 0 8px; }
  p { margin-bottom: 10px; }
  hr { border: none; border-top: 1px solid ${border.light}; margin: 16px 0; }
  ul, ol { padding-left: 24px; margin-bottom: 10px; }
  strong { color: ${text.primary}; }
`;

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${text.tertiary};
`;

export const TextInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  color: ${text.primary};
  outline: none;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s;

  &::placeholder { color: ${text.disabled}; }
  &:focus { border-color: ${brand.primary}; box-shadow: 0 0 0 2px rgba(12, 83, 187, 0.08); }
`;

export const DocNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;

  input {
    border-radius: ${radius.md} 0 0 ${radius.md};
    border-right: none;
  }
`;

export const DocNameSuffix = styled.span`
  padding: 9px 10px;
  background: ${surface.hover};
  border: 1px solid ${border.default};
  border-radius: 0 ${radius.md} ${radius.md} 0;
  font-size: 13px;
  color: ${text.tertiary};
  white-space: nowrap;
`;

// ---------------------------------------------------------------------------
// Client / Matter search
// ---------------------------------------------------------------------------

export const SearchWrapper = styled.div`
  position: relative;
`;

export const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 100;
`;

export const SearchResultItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid ${surface.page};
  transition: background 0.12s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${brand.primaryLight}; }
`;

export const SearchResultEmpty = styled.div`
  padding: 14px;
  text-align: center;
  color: ${text.disabled};
  font-size: 13px;
`;

export const ResultName = styled.div`
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const ResultMeta = styled.div`
  font-size: 12px;
  color: ${text.tertiary};
  margin-top: 2px;
`;

export const SelectedCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f0f7ff;
  border: 1px solid #c3d5f1;
  border-radius: ${radius.md};
`;

export const ClearBtn = styled.button`
  background: none;
  border: none;
  color: ${text.tertiary};
  cursor: pointer;
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  border-radius: ${radius.sm};
  &:hover { color: ${status.failed.fg}; background: ${status.failed.bg}; }
  &:focus-visible { outline: 2px solid ${border.focus}; outline-offset: 1px; }
`;

export const AutoMatchBanner = styled.div`
  padding: 10px 12px;
  background: ${({ matchType }) =>
    matchType === "single_matter" ? status.ready.bg : status.review.bg};
  border: 1px solid ${({ matchType }) =>
    matchType === "single_matter" ? status.ready.border : status.review.border};
  border-radius: ${radius.md};
  cursor: ${({ matchType }) => (matchType === "single_matter" ? "pointer" : "default")};
  transition: filter 0.15s;

  &:hover {
    filter: ${({ matchType }) =>
      matchType === "single_matter" ? "brightness(0.97)" : "none"};
  }
`;

export const AutoMatchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

export const AutoMatchLabel = styled.div`
  font-size: 10px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ matchType }) =>
    matchType === "single_matter" ? status.ready.fg : status.review.fg};
`;

export const ExplainPanel = styled.div`
  padding: 10px 12px;
  background: ${surface.folderTint};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* The sidebar column is narrow; long audit reasons scroll instead of
     pushing the action buttons below the fold. */
  max-height: 220px;
  overflow-y: auto;
`;

export const ExplainSignals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const ExplainSignal = styled.span`
  font-size: 10px;
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${status.ready.bg};
  border: 1px solid ${status.ready.border};
  color: ${status.ready.fg};
`;

export const ExplainReason = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${text.secondary};
`;

export const ExplainAltHeading = styled.div`
  font-size: 10px;
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${text.tertiary};
  margin-top: 2px;
`;

export const ExplainAltRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: ${text.secondary};
`;

export const MatterSelect = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  color: ${text.primary};
  background: ${surface.surface};
  outline: none;
  cursor: pointer;

  &:focus { border-color: ${brand.primary}; }
`;

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export const PrimaryBtn = styled.button`
  width: 100%;
  padding: 11px 20px;
  background: ${brand.primary};
  color: ${text.onAccent};
  border: none;
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: background 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) { background: ${brand.primaryHover}; }
  &:disabled { background: #c3d5f1; cursor: not-allowed; }
`;

export const SecondaryBtn = styled.button`
  flex: 1;
  padding: 9px 16px;
  background: ${surface.surface};
  color: ${text.tertiary};
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) { background: ${surface.hover}; color: ${text.primary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const DangerBtn = styled.button`
  flex: 1;
  padding: 9px 16px;
  background: ${surface.surface};
  color: ${status.failed.fg};
  border: 1px solid ${status.failed.border};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) { background: ${status.failed.bg}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const BtnRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const KbdHint = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  font-family: ${typography.fontFamily};
  font-size: 10px;
  font-weight: ${typography.weight.semibold};
  color: inherit;
  background: rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: ${radius.sm};
  opacity: 0.75;
  margin-left: 4px;
`;

// ---------------------------------------------------------------------------
// Empty / loading states
// ---------------------------------------------------------------------------

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
  flex: 1;
`;

export const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: ${typography.weight.semibold};
  color: ${text.tertiary};
  margin-bottom: 8px;
`;

export const EmptyText = styled.div`
  font-size: 13px;
  color: ${text.disabled};
  max-width: 300px;
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${text.disabled};
  font-size: 13px;
  flex: 1;
`;

// ---------------------------------------------------------------------------
// Sidebar skeleton (rendered while currentItemLoading between documents)
// ---------------------------------------------------------------------------

// Use styled-components' `keyframes` helper (matches
// MatterDetailsSkeleton/MatterListSkeleton) so the animation name is
// uniquely scoped and deduplicated in the generated CSS instead of
// leaking a global `intake-shimmer` identifier on every instance.
const shimmerKeyframes = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const shimmer = css`
  background: linear-gradient(
    90deg,
    ${surface.hover} 0%,
    ${border.light} 50%,
    ${surface.hover} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmerKeyframes} 1.2s ease-in-out infinite;
`;

// $h: number → "<n>px"
// $w: number → "<n>px", string → passed through (so "60%" still works)
const toCssLen = (v, fallback) => {
  if (v == null) return fallback;
  return typeof v === "number" ? `${v}px` : v;
};

export const SkeletonBlock = styled.div`
  height: ${({ $h }) => toCssLen($h, "12px")};
  width: ${({ $w }) => toCssLen($w, "100%")};
  border-radius: ${radius.sm};
  ${shimmer};
`;

export const SkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

export const ListViewWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  overflow: hidden;
`;

export const ListToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const ListSearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 360px;
  padding: 8px 14px;
  border: 1px solid ${border.default};
  border-radius: ${radius.md};
  font-family: ${typography.fontFamily};
  font-size: 13px;
  color: ${text.primary};
  outline: none;

  &::placeholder { color: ${text.disabled}; }
  &:focus { border-color: ${brand.primary}; }
`;

export const ListTableWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

export const ListFooter = styled.div`
  padding-top: 16px;
`;

// StatusPill backs the Mail Intake list's Status column. Every
// MailIntakeItem.Status on the server has an entry here (uploading /
// pending / claimed / skipped / assigned / deleted). Tones are mapped to
// semantic tokens so the intake queue reads consistently with the rest
// of the design system (processing/review/ready/closed/failed/neutral).
const INTAKE_STATUS_TONES = {
  uploading: status.processing,
  pending: status.review,
  claimed: status.processing,
  skipped: status.neutral,
  assigned: status.ready,
  deleted: status.failed,
};

export const StatusPill = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: ${radius.xl};
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;

  ${({ status: s }) => {
    const tone = INTAKE_STATUS_TONES[s] || status.neutral;
    return css`
      background: ${tone.bg};
      color: ${tone.fg};
      border: 1px solid ${tone.border};
    `;
  }}
`;

export const MutedText = styled.span`
  color: ${text.disabled};
`;

export const MetaText = styled.span`
  color: ${text.tertiary};
  font-size: 13px;
`;

// ---------------------------------------------------------------------------
// Upload view
// ---------------------------------------------------------------------------

export const UploadViewWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;

export const DropZone = styled.div`
  width: 100%;
  max-width: 600px;
  padding: 60px 40px;
  border: 2px dashed ${({ active }) => (active ? brand.primary : border.default)};
  border-radius: ${radius.xl};
  background: ${({ active }) => (active ? brand.primaryLight : "#fafbfc")};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ active }) => (active ? brand.primary : text.disabled)};

  &:hover {
    border-color: ${brand.primary};
    background: #f8faff;
  }
`;

export const DropZoneTitle = styled.div`
  font-size: 16px;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  margin-bottom: 8px;
`;

export const DropZoneText = styled.div`
  font-size: 13px;
  color: ${text.disabled};
`;

export const UploadFileList = styled.div`
  width: 100%;
  max-width: 600px;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const UploadFileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f9fafb;
  border: 1px solid ${border.light};
  border-radius: ${radius.md};
  font-size: 13px;
  color: ${text.primary};
`;

export const UploadRemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${text.tertiary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 2px 4px;
  border-radius: ${radius.sm};
  &:hover { color: ${status.failed.fg}; background: ${status.failed.bg}; }
  &:focus-visible { outline: 2px solid ${border.focus}; outline-offset: 1px; }
`;

export const UploadProgress = styled.div`
  height: 4px;
  background: ${border.light};
  border-radius: ${radius.sm};
  overflow: hidden;
  margin-top: 6px;
`;

export const UploadProgressBar = styled.div`
  height: 100%;
  background: ${brand.primary};
  border-radius: ${radius.sm};
  transition: width 0.3s;
  width: ${({ percent }) => percent || 0}%;
`;

// ---------------------------------------------------------------------------
// Stats view
// ---------------------------------------------------------------------------

export const StatsViewWrapper = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

export const StatCard = styled.div`
  padding: 20px;
  background: #f9fafb;
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
`;

export const StatValue = styled.div`
  font-size: 28px;
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  margin-bottom: 4px;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  font-weight: ${typography.weight.semibold};
  color: ${text.disabled};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const SorterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid ${surface.page};

  &:last-child { border-bottom: none; }
`;

export const SorterAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${brand.primary};
  color: ${text.onAccent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: ${typography.weight.bold};
  flex-shrink: 0;
`;

export const SorterInfo = styled.div`
  flex: 1;
`;

export const SorterName = styled.div`
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const SorterStat = styled.div`
  font-size: 12px;
  color: ${text.tertiary};
  margin-top: 2px;
`;

export const SorterCount = styled.div`
  font-size: 20px;
  font-weight: ${typography.weight.bold};
  color: ${brand.primary};
  text-align: right;
  min-width: 50px;
`;
